import {
  DashboardIcon,
  HelpIcon,
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { FeatureFlagged } from '@backstage/core-app-api';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import AssessmentIcon from '@material-ui/icons/Assessment';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MapIcon from '@material-ui/icons/MyLocation';
import MenuIcon from '@material-ui/icons/Menu';
import WidgetsIcon from '@material-ui/icons/Widgets';
import ExtensionIcon from '@material-ui/icons/Extension';
import { PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import ToolsIcon from '@material-ui/icons/Build';
import AddBoxIcon from '@material-ui/icons/AddBox';
import { SidebarLogo } from './SidebarLogo';
import { deploymentsPlugin } from '@appfolio/backstage-plugin-deployments';
import { utilitiesPagePlugin } from '@appfolio/backstage-plugin-utilities-page';
import { SearchIcon, SidebarSearch } from '@appfolio/backstage-plugin-search';
import {
  RocketIcon,
  DocsIcon,
  DxIcon,
  SupernovaIcon,
} from '@appfolio/backstage-components';
import PeopleIcon from '@material-ui/icons/People';
import { Helmet } from 'react-helmet';
import { useVersionCheck } from '../../features/version-check';
import { FEATURE_FLAG_SHOW_CREATE_BUTTON } from '@appfolio/backstage-plugin-navigation-common';

export const Root = ({ children }: PropsWithChildren<{}>) => {
  useVersionCheck();
  const location = useLocation();
  const getDeploymentsHref = useRouteRef(deploymentsPlugin.routes.root);
  const getUtilitiesPageHref = useRouteRef(utilitiesPagePlugin.routes.root);
  return (
    <>
      <Helmet>
        <link rel="icon" href="/favicon.ico" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link
          rel="apple-touch-icon"
          sizes="192x192"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link
          rel="manifest"
          href="/manifest.json"
          crossOrigin="use-credentials"
        />
      </Helmet>
      <SidebarPage>
        <Sidebar>
          <SidebarLogo to="/" />
          {!/^\/search(\/|$)/i.test(location.pathname) ? (
            <SidebarGroup label="Search" icon={<SearchIcon />}>
              <SidebarSearch />
            </SidebarGroup>
          ) : null}
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarItem icon={HomeIcon} to="/" text="Home" />
            <SidebarItem icon={HelpIcon} to="/help" text="Help" />
            <SidebarDivider />
            <SidebarItem icon={WidgetsIcon} to="catalog" text="Catalog" />
            <SidebarItem
              icon={PeopleIcon}
              to="topology"
              text="People & Teams"
            />
            <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
            <SidebarScrollWrapper>
              <SidebarItem
                icon={RocketIcon}
                to={getDeploymentsHref()}
                text="Deployments"
              />
              <FeatureFlagged with={FEATURE_FLAG_SHOW_CREATE_BUTTON}>
                <SidebarItem icon={AddBoxIcon} to="create" text="Create" />
              </FeatureFlagged>
            </SidebarScrollWrapper>
            <SidebarDivider />
            <SidebarScrollWrapper>
              <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
              <SidebarItem
                icon={DocsIcon}
                to="/docs/appfolio/resource/appfolio-software-development-guide/"
                text="Developer Guide"
              />
            </SidebarScrollWrapper>
            <SidebarDivider />
            <SidebarItem
              target="_blank"
              icon={DxIcon}
              to="https://appfolio.okta.com/home/dev-62948209_dx_3/0oa1hkcmxtsYgmjxY0x8/alno3bxfntOoHgsgi5d7"
              text="Insights"
            />
            <SidebarItem
              icon={AssessmentIcon}
              to="/analytics/focus"
              text="Analytics (Old)"
            />
            <SidebarDivider />
            <SidebarScrollWrapper>
              <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
              <SidebarItem
                target="_blank"
                icon={SupernovaIcon}
                to="https://supernova.dx.appf.io/"
                text="Supernova"
              />
              <SidebarItem icon={ToolsIcon} to="tools" text="Developer Tools" />
            </SidebarScrollWrapper>
            <SidebarDivider />
            <SidebarScrollWrapper>
              <SidebarItem
                icon={DashboardIcon}
                to={getUtilitiesPageHref()}
                text="Control Panel"
              />
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>
        </Sidebar>
        {children}
      </SidebarPage>
    </>
  );
};
